import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { hubApi, PLAN_PRICING, STATUS_STYLE, calcFinalAmount } from "../api.js";
import { useIsMobile } from "../hooks/useIsMobile.js";

const fmt = d => d ? new Date(d).toLocaleDateString("ro-RO", { day:"2-digit", month:"short", year:"numeric" }) : "—";
const daysUntil = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

export default function DashboardPage() {
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const [restaurants, setRestaurants] = useState([]);
  const [search, setSearch]           = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlan, setFilterPlan]   = useState("all");
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    hubApi.getAll()
      .then(({ data }) => setRestaurants(data.restaurants || []))
      .catch(() => toast.error("Nu s-au putut încărca restaurantele."))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (e, id) => {
    e.stopPropagation();
    try {
      const { data } = await hubApi.toggle(id);
      if (data.success) setRestaurants(r => r.map(x => x._id === id ? { ...x, isActive: data.isActive } : x));
    } catch { toast.error("Eroare."); }
  };

  const list = Array.isArray(restaurants) ? restaurants : [];
  const active  = list.filter(r => r.isActive);
  const mrr = active.reduce((sum, r) => {
    const base = calcFinalAmount(r.plan, r.billingPeriod||"monthly", r.discountType, r.discountValue);
    const m = r.billingPeriod==="yearly" ? base/12 : r.billingPeriod==="quarterly" ? base/3 : base;
    return sum + m;
  }, 0);
  const overdue  = list.filter(r => r.billingStatus === "overdue");
  const upcoming = list.filter(r => {
    if (!r.currentDueDate || r.billingStatus==="paid") return false;
    const d = daysUntil(r.currentDueDate);
    return d !== null && d >= 0 && d <= 30;
  }).sort((a,b) => new Date(a.currentDueDate)-new Date(b.currentDueDate));

  const filtered = list.filter(r => {
    const q = search.toLowerCase();
    if (q && !r.name.toLowerCase().includes(q) && !r.slug.toLowerCase().includes(q)) return false;
    if (filterStatus==="active"   && !r.isActive) return false;
    if (filterStatus==="inactive" && r.isActive)  return false;
    if (filterStatus==="overdue"  && r.billingStatus!=="overdue") return false;
    if (filterStatus==="pending"  && r.billingStatus!=="pending") return false;
    if (filterPlan!=="all" && String(r.plan)!==filterPlan) return false;
    return true;
  });

  const activeRate = list.length ? Math.round(active.length / list.length * 100) : 0;

  return (
    <div style={{ minHeight:"100vh", background:"#f0f2f5", fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif" }}>
      <style>{`
        *,*::before,*::after{box-sizing:border-box;}
        @keyframes spin{to{transform:rotate(360deg);}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
        .dash-animate{animation:fadeUp 0.35s ease both;}
        .r-card{transition:box-shadow 0.18s,transform 0.18s,border-color 0.18s;}
        .r-card:hover{box-shadow:0 8px 24px rgba(0,0,0,0.09) !important;transform:translateY(-2px);border-color:#c7d2fe !important;}
        .kpi-card{transition:transform 0.18s,box-shadow 0.18s;}
        .kpi-card:hover{transform:translateY(-2px);box-shadow:0 12px 32px rgba(0,0,0,0.12);}
        .action-btn:hover{filter:brightness(0.92);}
        .sidebar-item:hover{background:#f8faff !important;}
        .filter-select{appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23697386' stroke-width='2.5' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 10px center;padding-right:28px !important;}
        .search-input:focus{border-color:#6366f1 !important;box-shadow:0 0 0 3px rgba(99,102,241,0.1);}
        .pill-tab{transition:all 0.15s;}
        .pill-tab:hover{background:#e8eaf6;}
      `}</style>

      {/* KPI Header */}
      <div style={{ background:"linear-gradient(135deg,#1e1b4b 0%,#1d4ed8 60%,#4f46e5 100%)", padding: isMobile ? "24px 16px 32px" : "32px 32px 48px" }}>
        <div style={{ marginBottom: isMobile ? 20 : 28 }}>
          <h2 style={{ margin:0, fontSize: isMobile ? 18 : 22, fontWeight:800, color:"#fff", letterSpacing:"-0.4px" }}>
            Bun venit, Admin 👋
          </h2>
          <p style={{ margin:"4px 0 0", fontSize:13, color:"rgba(255,255,255,0.55)" }}>
            {new Date().toLocaleDateString("ro-RO", { weekday:"long", day:"2-digit", month:"long", year:"numeric" })}
          </p>
        </div>

        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: isMobile ? 10 : 16 }}>
          <KpiCard
            label="Restaurante active"
            value={active.length}
            sub={`${activeRate}% din ${list.length} total`}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}
            accent="#22d3ee"
            accentBg="rgba(34,211,238,0.15)"
          />
          <KpiCard
            label="MRR estimat"
            value={`€${Math.round(mrr).toLocaleString("ro-RO")}`}
            sub="venit lunar recurent"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}
            accent="#a78bfa"
            accentBg="rgba(167,139,250,0.15)"
          />
          <KpiCard
            label="ARR estimat"
            value={`€${Math.round(mrr*12).toLocaleString("ro-RO")}`}
            sub="venit anual recurent"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>}
            accent="#34d399"
            accentBg="rgba(52,211,153,0.15)"
          />
          <KpiCard
            label="Restanțe"
            value={overdue.length}
            sub={overdue.length===0 ? "totul în regulă ✓" : `${overdue.length} cont${overdue.length!==1?"uri":""} restante`}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
            accent={overdue.length>0 ? "#f87171" : "#4ade80"}
            accentBg={overdue.length>0 ? "rgba(248,113,113,0.15)" : "rgba(74,222,128,0.15)"}
            alert={overdue.length>0}
          />
        </div>
      </div>

      {/* Content (pulls up to overlap header) */}
      <div style={{ padding: isMobile ? "0 12px 24px" : "0 32px 32px", marginTop: isMobile ? -16 : -24 }}>
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 296px", gap:20, alignItems:"start" }}>

          {/* LEFT — Restaurant list */}
          <div className="dash-animate" style={{ animationDelay:"0.05s" }}>

            {/* Filters bar */}
            <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e8eaf0", padding:"12px 14px", marginBottom:14, display:"flex", gap:8, flexWrap:"wrap", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ position:"relative", flex:1, minWidth:140 }}>
                <svg style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input className="search-input" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Caută restaurant sau slug..."
                  style={{ width:"100%", padding:"9px 12px 9px 34px", borderRadius:9, border:"1.5px solid #e5e7eb", background:"#f9fafb", fontSize:13, outline:"none", color:"#111827", transition:"border-color 0.15s, box-shadow 0.15s" }}/>
              </div>
              <select className="filter-select" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
                style={{ padding:"9px 12px", borderRadius:9, border:"1.5px solid #e5e7eb", background:"#f9fafb", fontSize:13, color:"#374151", outline:"none", cursor:"pointer" }}>
                <option value="all">Toate statusurile</option>
                <option value="active">Activ</option>
                <option value="inactive">Inactiv</option>
                <option value="overdue">Restanță</option>
                <option value="pending">În așteptare</option>
              </select>
              <select className="filter-select" value={filterPlan} onChange={e=>setFilterPlan(e.target.value)}
                style={{ padding:"9px 12px", borderRadius:9, border:"1.5px solid #e5e7eb", background:"#f9fafb", fontSize:13, color:"#374151", outline:"none", cursor:"pointer" }}>
                <option value="all">Toate planurile</option>
                <option value="1">Light</option>
                <option value="2">Pro</option>
                <option value="3">Enterprise</option>
              </select>
            </div>

            {/* Section header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <span style={{ fontSize:13, fontWeight:700, color:"#374151" }}>
                Restaurante{filtered.length!==list.length ? ` (${filtered.length}/${list.length})` : ` (${list.length})`}
              </span>
              <button onClick={()=>nav("/restaurants/new")}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 14px", borderRadius:8, border:"none", background:"#4f46e5", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Adaugă restaurant
              </button>
            </div>

            {loading ? (
              <div style={{ padding:"60px 0", textAlign:"center" }}>
                <svg style={{ animation:"spin 0.8s linear infinite" }} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                <p style={{ margin:"10px 0 0", fontSize:13, color:"#9ca3af" }}>Se încarcă...</p>
              </div>
            ) : filtered.length===0 ? (
              <div style={{ padding:"48px 24px", textAlign:"center", background:"#fff", borderRadius:14, border:"1px solid #e8eaf0" }}>
                <div style={{ width:48, height:48, borderRadius:12, background:"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </div>
                <p style={{ margin:0, fontSize:14, fontWeight:600, color:"#374151" }}>{list.length===0 ? "Niciun restaurant adăugat" : "Niciun rezultat"}</p>
                <p style={{ margin:"4px 0 0", fontSize:13, color:"#9ca3af" }}>{list.length===0 ? "Adaugă primul restaurant pentru a începe." : "Încearcă alte filtre."}</p>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {filtered.map((r, idx) => {
                  const plan   = PLAN_PRICING[r.plan] || PLAN_PRICING[1];
                  const ss     = STATUS_STYLE[r.billingStatus] || STATUS_STYLE.pending;
                  const days   = daysUntil(r.currentDueDate);
                  const urgent = r.billingStatus!=="paid" && days!==null && days<=7;
                  const amount = r.currentAmount ? `${Number(r.currentAmount).toLocaleString("ro-RO")} ${r.currentCurrency||"EUR"}` : null;

                  return (
                    <div key={r._id} className="r-card dash-animate"
                      style={{ animationDelay:`${0.05 + idx*0.04}s` }}
                      onClick={()=>nav(`/restaurants/${r._id}`)}>
                      <div style={{ background:"#fff", borderRadius:13, border:`1.5px solid ${r.billingStatus==="overdue"?"#fca5a5":"#e8eaf0"}`, padding: isMobile ? "14px" : "16px 20px", cursor:"pointer", display:"flex", alignItems:"center", gap: isMobile ? 10 : 16, boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>

                        {/* Avatar */}
                        <div style={{ width: isMobile ? 38 : 46, height: isMobile ? 38 : 46, borderRadius:12, background:`linear-gradient(135deg,${plan.color}22,${plan.color}44)`, border:`2px solid ${plan.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize: isMobile ? 15 : 18, fontWeight:800, color:plan.color, flexShrink:0, letterSpacing:"-0.5px" }}>
                          {r.name.charAt(0).toUpperCase()}
                        </div>

                        {/* Nume + slug */}
                        <div style={{ minWidth:0, flex: isMobile ? "1" : "0 0 180px" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                            <p style={{ margin:0, fontWeight:700, fontSize:14, color:"#111827", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.name}</p>
                            {!r.isActive && <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:99, background:"#fef2f2", color:"#dc2626", border:"1px solid #fca5a5", flexShrink:0 }}>OFF</span>}
                          </div>
                          <p style={{ margin:0, fontSize:11, color:"#9ca3af", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.slug}.orderly.ro</p>
                        </div>

                        {/* Plan + Status badges — desktop */}
                        {!isMobile && (
                          <div style={{ display:"flex", gap:6, flex:"0 0 auto" }}>
                            <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99, background:plan.bg, color:plan.color, border:`1px solid ${plan.border}` }}>{plan.name}</span>
                            <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99, background:ss.bg, color:ss.color, border:`1px solid ${ss.border}` }}>{ss.label}</span>
                          </div>
                        )}

                        {/* Sumă — desktop */}
                        {!isMobile && (
                          <div style={{ flex:1, textAlign:"center" }}>
                            {amount
                              ? <p style={{ margin:0, fontSize:15, fontWeight:800, color:"#111827", letterSpacing:"-0.3px" }}>{amount}</p>
                              : <p style={{ margin:0, fontSize:13, color:"#d1d5db" }}>—</p>}
                            <p style={{ margin:"2px 0 0", fontSize:10, color:"#9ca3af", fontWeight:500 }}>sumă curentă</p>
                          </div>
                        )}

                        {/* Scadent — desktop */}
                        {!isMobile && (
                          <div style={{ flex:"0 0 100px", textAlign:"center" }}>
                            <p style={{ margin:0, fontSize:13, fontWeight:urgent?700:500, color:urgent?"#dc2626":"#374151" }}>{fmt(r.currentDueDate)}</p>
                            {days!==null && r.billingStatus!=="paid" && (
                              <p style={{ margin:"2px 0 0", fontSize:10, fontWeight:600, color:days<0?"#dc2626":days<=7?"#d97706":"#9ca3af" }}>
                                {days<0?`${Math.abs(days)}z restanță`:days===0?"azi":days===1?"mâine":`${days} zile`}
                              </p>
                            )}
                            {r.billingStatus==="paid" && <p style={{ margin:"2px 0 0", fontSize:10, color:"#10b981", fontWeight:600 }}>achitat ✓</p>}
                          </div>
                        )}

                        {/* Mobile badges */}
                        {isMobile && (
                          <div style={{ display:"flex", flexDirection:"column", gap:4, alignItems:"flex-end" }}>
                            <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:99, background:plan.bg, color:plan.color }}>{plan.name}</span>
                            <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:99, background:ss.bg, color:ss.color }}>{ss.label}</span>
                          </div>
                        )}

                        {/* Acțiuni */}
                        <div style={{ display:"flex", alignItems:"center", gap:8, flex:"0 0 auto" }} onClick={e=>e.stopPropagation()}>
                          {!isMobile && (
                            <button onClick={()=>nav(`/restaurants/${r._id}`)}
                              style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 13px", borderRadius:8, border:"1.5px solid #e0e7ff", background:"#eef2ff", color:"#4f46e5", fontSize:12, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.15s" }}
                              onMouseEnter={e=>{e.currentTarget.style.background="#4f46e5";e.currentTarget.style.color="#fff";}}
                              onMouseLeave={e=>{e.currentTarget.style.background="#eef2ff";e.currentTarget.style.color="#4f46e5";}}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                              Facturează
                            </button>
                          )}
                          <Toggle active={r.isActive} onToggle={e=>handleToggle(e, r._id)}/>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT sidebar */}
          <div className="dash-animate" style={{ display:"flex", flexDirection:"column", gap:14, animationDelay:"0.1s" }}>

            {/* Quick Actions */}
            <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e8eaf0", padding:"16px", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
              <p style={{ fontSize:12, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.8px", margin:"0 0 12px" }}>Acțiuni rapide</p>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {[
                  { icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>, label:"Adaugă restaurant", color:"#4f46e5", bg:"#eef2ff", path:"/restaurants/new" },
                  { icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>, label:"Conturi clienți", color:"#0891b2", bg:"#ecfeff", path:"/client-accounts" },
                  { icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>, label:"Tickete suport", color:"#7c3aed", bg:"#f5f3ff", path:"/tickets" },
                  { icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>, label:"Note de actualizare", color:"#059669", bg:"#ecfdf5", path:"/update-notes" },
                ].map((a,i)=>(
                  <button key={i} onClick={()=>nav(a.path)}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:9, border:"1px solid #f0f0f0", background:"#fafafa", cursor:"pointer", textAlign:"left", transition:"all 0.15s", width:"100%" }}
                    onMouseEnter={e=>{e.currentTarget.style.background=a.bg;e.currentTarget.style.borderColor=a.color+"44";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="#fafafa";e.currentTarget.style.borderColor="#f0f0f0";}}>
                    <div style={{ width:28, height:28, borderRadius:8, background:a.bg, display:"flex", alignItems:"center", justifyContent:"center", color:a.color, flexShrink:0 }}>
                      {a.icon}
                    </div>
                    <span style={{ fontSize:13, fontWeight:600, color:"#374151" }}>{a.label}</span>
                    <svg style={{ marginLeft:"auto", color:"#d1d5db" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                ))}
              </div>
            </div>

            {/* Scadente */}
            <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e8eaf0", overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ padding:"14px 16px 13px", borderBottom:"1px solid #f3f4f6", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ fontSize:13, fontWeight:700, color:"#111827" }}>Scadente în 30 zile</span>
                {upcoming.length>0 && (
                  <span style={{ fontSize:11, fontWeight:700, padding:"2px 9px", borderRadius:99, background:"#fffbeb", color:"#d97706", border:"1px solid #fde68a" }}>{upcoming.length}</span>
                )}
              </div>
              {upcoming.length===0 ? (
                <div style={{ padding:"24px 16px", textAlign:"center" }}>
                  <div style={{ fontSize:24, marginBottom:6 }}>🎉</div>
                  <p style={{ margin:0, fontSize:13, color:"#9ca3af" }}>Nicio plată scadentă</p>
                </div>
              ) : upcoming.map(r=>{
                const days=daysUntil(r.currentDueDate);
                const plan=PLAN_PRICING[r.plan]||PLAN_PRICING[1];
                return (
                  <div key={r._id} className="sidebar-item"
                    onClick={()=>nav(`/restaurants/${r._id}`)}
                    style={{ padding:"11px 16px", borderBottom:"1px solid #f9fafb", cursor:"pointer", display:"flex", alignItems:"center", gap:10, transition:"background 0.12s" }}>
                    <div style={{ width:32, height:32, borderRadius:9, background:plan.bg, border:`1.5px solid ${plan.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:plan.color, flexShrink:0 }}>
                      {r.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ margin:0, fontSize:13, fontWeight:600, color:"#111827", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.name}</p>
                      <p style={{ margin:"1px 0 0", fontSize:11, color:"#9ca3af" }}>{fmt(r.currentDueDate)}</p>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <p style={{ margin:0, fontSize:12, fontWeight:700, color:"#111827" }}>{r.currentAmount?`${r.currentAmount} ${r.currentCurrency||"EUR"}`:"—"}</p>
                      <p style={{ margin:0, fontSize:10, fontWeight:700, color:days<=3?"#dc2626":days<=7?"#d97706":"#9ca3af" }}>
                        {days===0?"azi":days===1?"mâine":`${days}z`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Distribuție plan */}
            <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e8eaf0", padding:"16px", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
              <p style={{ fontSize:12, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.8px", margin:"0 0 14px" }}>Distribuție plan</p>
              {[1,2,3].map(pid=>{
                const p=PLAN_PRICING[pid];
                const cnt=list.filter(r=>r.plan===pid).length;
                const pct=list.length?Math.round(cnt/list.length*100):0;
                return (
                  <div key={pid} style={{ marginBottom:14 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:7 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                        <div style={{ width:8, height:8, borderRadius:"50%", background:p.color }}/>
                        <span style={{ fontSize:12, fontWeight:700, color:"#374151" }}>{p.name}</span>
                      </div>
                      <span style={{ fontSize:12, fontWeight:600, color:"#9ca3af" }}>{cnt} · {pct}%</span>
                    </div>
                    <div style={{ height:5, borderRadius:99, background:"#f3f4f6", overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,${p.color}cc,${p.color})`, borderRadius:99, transition:"width 0.6s ease" }}/>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Restanțe alert */}
            {overdue.length>0 && (
              <div style={{ background:"linear-gradient(135deg,#fef2f2,#fff5f5)", borderRadius:14, border:"1px solid #fca5a5", padding:"16px", boxShadow:"0 1px 4px rgba(220,38,38,0.08)" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                  <div style={{ width:28, height:28, borderRadius:8, background:"#dc2626", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  </div>
                  <p style={{ margin:0, fontSize:13, fontWeight:700, color:"#dc2626" }}>Restanțe ({overdue.length})</p>
                </div>
                {overdue.map(r=>(
                  <div key={r._id} onClick={()=>nav(`/restaurants/${r._id}`)}
                    style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"1px solid #fecaca", cursor:"pointer" }}>
                    <span style={{ fontSize:13, color:"#991b1b", fontWeight:500 }}>{r.name}</span>
                    <span style={{ fontSize:13, color:"#dc2626", fontWeight:700 }}>{r.currentAmount?`${r.currentAmount} ${r.currentCurrency||"EUR"}`:"—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon, accent, accentBg, alert }) {
  return (
    <div className="kpi-card" style={{ background:"rgba(255,255,255,0.1)", backdropFilter:"blur(10px)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:14, padding:"16px 20px", position:"relative", overflow:"hidden" }}>
      {alert && <div style={{ position:"absolute", top:12, right:12, width:8, height:8, borderRadius:"50%", background:"#f87171", boxShadow:"0 0 8px #f87171" }}/>}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
        <div style={{ width:34, height:34, borderRadius:9, background:accentBg, display:"flex", alignItems:"center", justifyContent:"center", color:accent }}>
          {icon}
        </div>
        <span style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.55)", letterSpacing:"0.3px" }}>{label}</span>
      </div>
      <div style={{ fontSize:28, fontWeight:800, color:"#fff", letterSpacing:"-1px", lineHeight:1, fontVariantNumeric:"tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:5, fontWeight:500 }}>{sub}</div>}
    </div>
  );
}

function Toggle({ active, onToggle }) {
  return (
    <button onClick={onToggle} title={active?"Dezactivează":"Activează"}
      style={{ width:40, height:22, borderRadius:99, border:"none", cursor:"pointer", padding:2, background:active?"#10b981":"#e5e7eb", transition:"background 0.2s", display:"flex", alignItems:"center", justifyContent:active?"flex-end":"flex-start", flexShrink:0, boxShadow:"inset 0 1px 2px rgba(0,0,0,0.1)" }}>
      <div style={{ width:18, height:18, borderRadius:"50%", background:"#fff", boxShadow:"0 1px 4px rgba(0,0,0,0.25)", transition:"all 0.2s" }}/>
    </button>
  );
}
